import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Ruler, Umbrella, Utensils, BatteryCharging, Sun, Lightbulb, Type, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";
import Cart3D from "@/components/Cart3D";
import useSeo from "@/lib/useSeo";

const COLOR_MAP = {
    navy_gold: { primary: "#1E3A5F", accent: "#E0AE32" },
    yellow_green: { primary: "#F59E0B", accent: "#16A34A" },
    red_white: { primary: "#DC2626", accent: "#F8FAFC" },
    black_white: { primary: "#1F2937", accent: "#F8FAFC" },
    cream: { primary: "#E8DFC8", accent: "#0B2545" },
};

export default function ConfiguracaoPartilhada() {
    const { id } = useParams();
    const { tr } = useLocale();
    const [config, setConfig] = useState(null);
    const [error, setError] = useState(false);

    useSeo({
        title: tr("Configuração NOVIFOOD partilhada", "Shared NOVIFOOD configuration"),
        description: tr("Vê o food cart que configurei na NOVIFOOD — e configura o teu.", "See the food cart I configured at NOVIFOOD — and build yours."),
        path: `/c/${id}`,
        image: `/api/cart-configurator/${id}/og.png`,
    });

    useEffect(() => {
        api.get(`/cart-configurator/${id}/public`)
            .then(({ data }) => setConfig(data))
            .catch(() => setError(true));
    }, [id]);

    async function share() {
        const url = `${window.location.origin}/api/share/${id}`;
        const text = tr("Vê o food cart que configurei na NOVIFOOD!", "Check out the food cart I configured at NOVIFOOD!");
        if (navigator.share) {
            try { await navigator.share({ title: "NOVIFOOD", text, url }); return; } catch { /* cancelled */ }
        }
        try {
            await navigator.clipboard.writeText(url);
            toast.success(tr("Link copiado!", "Link copied!"));
        } catch {
            toast.info(tr("Copie o link: ", "Copy the link: ") + url, { duration: 10000 });
        }
    }

    if (error) {
        return (
            <div className="container-x py-32 text-center" data-testid="shared-config-notfound">
                <h1 className="font-heading text-3xl font-extrabold text-primary">{tr("Configuração não encontrada", "Configuration not found")}</h1>
                <p className="mt-3 text-muted-foreground">{tr("Este link pode ter expirado ou estar incorrecto.", "This link may have expired or be incorrect.")}</p>
                <Link to="/simulador" className="mt-8 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground">
                    {tr("Criar a minha configuração", "Create my configuration")} <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        );
    }

    if (!config) {
        return <div className="container-x py-32 text-center text-muted-foreground">{tr("A carregar...", "Loading...")}</div>;
    }

    const L = config.labels || {};
    const rows = [
        { icon: Ruler, label: tr("Tamanho", "Size"), value: L.size },
        { icon: Umbrella, label: tr("Cobertura", "Canopy"), value: config.canopy_type === "none" ? L.canopy : `${L.canopy} · ${L.canopy_color}` },
        { icon: Utensils, label: tr("Equipamento", "Equipment"), value: (L.addons || []).join(", ") || "—" },
        { icon: BatteryCharging, label: tr("Bateria", "Battery"), value: L.battery },
        { icon: Sun, label: tr("Painel solar", "Solar panel"), value: L.solar },
        { icon: Lightbulb, label: tr("Iluminação", "Lighting"), value: (L.lighting || []).join(", ") || "—" },
        ...(config.brand_name ? [{ icon: Type, label: "Branding", value: config.brand_name }] : []),
    ];

    return (
        <div className="bg-muted/40" data-testid="shared-config-page">
            <section className="gradient-navy text-white">
                <div className="container-x py-16 lg:py-20">
                    <span className="eyebrow text-secondary">{tr("Configuração partilhada", "Shared configuration")}</span>
                    <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight max-w-3xl">
                        {config.brand_name
                            ? tr(`O futuro carrinho "${config.brand_name}".`, `The future "${config.brand_name}" cart.`)
                            : tr("Um NOVIFOOD configurado à medida.", "A custom-configured NOVIFOOD.")}
                    </h1>
                    <p className="mt-4 text-white/75 max-w-xl">
                        {tr("Alguém desenhou este food cart no nosso simulador — e tu podes criar o teu em minutos.", "Someone designed this food cart in our configurator — and you can build yours in minutes.")}
                    </p>
                </div>
            </section>

            <section className="container-x py-12 lg:py-16">
                <div className="grid gap-8 lg:grid-cols-2 items-start">
                    <Cart3D
                        sizeId={config.size}
                        canopyType={config.canopy_type}
                        colors={COLOR_MAP[config.canopy_color] || COLOR_MAP.navy_gold}
                        hasSolar={config.solar !== "none"}
                        hasLighting={(config.lighting || []).length > 0}
                        hasFreezer={(config.addons || []).includes("freezer")}
                    />
                    <div className="rounded-2xl border border-border bg-white p-6 lg:p-8">
                        <div className="flex items-center justify-between">
                            <span className="eyebrow">{tr("Resumo", "Summary")}</span>
                            <span className="text-xs text-muted-foreground">{config.created_at}</span>
                        </div>
                        <div className="mt-5 space-y-4">
                            {rows.map((r) => (
                                <div key={r.label} className="flex items-start gap-3 pb-4 border-b border-border last:border-0 last:pb-0">
                                    <r.icon className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{r.label}</p>
                                        <p className="text-sm font-semibold text-primary mt-0.5">{r.value}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Link to="/simulador" data-testid="shared-config-cta"
                                className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:-translate-y-0.5 transition">
                                {tr("Configurar o meu carrinho", "Configure my own cart")} <ArrowRight className="h-4 w-4" />
                            </Link>
                            <button onClick={share} data-testid="shared-config-share"
                                className="inline-flex items-center gap-2 rounded-md border border-primary px-5 py-3 text-sm font-bold text-primary hover:bg-primary hover:text-primary-foreground transition">
                                <Share2 className="h-4 w-4" /> {tr("Partilhar", "Share")}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
